import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '../components/ui/Drawer';
import Modal from '../components/ui/Modal';
import Spinner from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import { createApplication, listApplications, listRankedApplications } from '../api/applications';
import { listJobOpenings } from '../api/jobOpenings';
import { createQuestionSet, listQuestionSets } from '../api/questionSets';
import { listQuestions } from '../api/questions';
import { listMyOrgs } from '../api/orgs';
import { listAptitudeTests } from '../api/aptitudeTests';

function scoreClass(score) {
  if (score == null) return 'text-ink-faint';
  if (score >= 70) return 'text-teal-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-rose-600';
}

// P0-09: the backend marks sessions the platform could not assess, or that a
// technical/ambiguous ending cut short. Neither is a candidate result.
function isTechnicalFailure(s) {
  return s?.interviewFeedback?.technicalFailure === true;
}
function isTechnicalInterruption(s) {
  return s?.interviewFeedback?.technicalInterruption === true;
}
function hrReviewReason(s) {
  if (s?.terminationReason === 'INTEGRITY_VIOLATION') return 'Interview ended under the integrity policy after an unreviewed automated signal (not a finding of misconduct) - flagged for HR review.';
  if (s?.terminationReason === 'ABANDONED_NON_SERIOUS') return 'Interview abandoned early - flagged for HR review.';
  if (isTechnicalFailure(s)) return 'Not assessed: a technical issue prevented scoring. This is not a candidate result - flagged for HR review.';
  if (isTechnicalInterruption(s)) return 'Interview interrupted by a technical or unclear ending (not a candidate outcome). Scored only on the questions reached - flagged for HR review.';
  return 'Flagged for HR review.';
}

function recommendationClass(rec) {
  const value = (rec || '').toUpperCase();
  if (value === 'STRONG_YES') return 'chip-teal-solid';
  if (value === 'YES') return 'chip-teal';
  if (value === 'MAYBE') return 'chip-amber';
  if (value === 'NO') return 'chip-rose';
  return 'chip-navy';
}

// I-P0-B: server integrity status (NONE / MEDIUM / HIGH) derived from normalized,
// UNREVIEWED signals. Never a verdict and never mixed into the capability score.
function integrityBadge(status) {
  const s = (status || '').toUpperCase();
  if (s === 'NONE') return { label: 'No review signals', cls: 'chip-navy' };
  if (s === 'MEDIUM') return { label: 'Review signals (medium)', cls: 'chip-amber' };
  if (s === 'HIGH') return { label: 'Review signals (high)', cls: 'chip-amber' };
  return { label: 'Not evaluated', cls: 'chip-navy' };
}

function statusClass(status) {
  const value = (status || '').toUpperCase();
  if (value === 'PENDING') return 'chip-navy';
  if (value === 'INVITED') return 'chip-teal';
  if (value === 'INTERVIEW_SCHEDULED') return 'chip-teal-solid';
  if (value === 'INTERVIEW_COMPLETED') return 'chip-teal';
  if (value === 'REJECTED' || value === 'APTITUDE_FAILED') return 'chip-rose';
  return 'chip-navy';
}

function statusLabel(status) {
  const value = (status || '').toUpperCase();
  if (value === 'APTITUDE_FAILED') return 'Aptitude Failed';
  if (value === 'INTERVIEW_SCHEDULED') return 'Interview Scheduled';
  if (value === 'INTERVIEW_COMPLETED') return 'Interview Completed';
  return status || '-';
}

function aptitudeSummary(app) {
  if (app?.aptitudeScore == null) return null;
  const passed = app.aptitudePassed === true;
  const failed = app.aptitudePassed === false;
  const outcome = passed ? 'Passed' : failed ? 'Failed' : 'Pending';
  return `Aptitude: ${app.aptitudeScore}% - ${outcome}`;
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function toList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value.split('\n').map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

const EMPTY_NEW_APP = {
  candidateName: '',
  candidateEmail: '',
  jobTitle: '',
  jobOpeningId: '',
  scheduledAt: '',
  questionSetId: '',
  aptitudeTestId: '',
  resumeUrl: '',
};

export default function ApplicationsPage() {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('rankings');
  const [search, setSearch] = useState('');
  const [rankings, setRankings] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const [newAppOpen, setNewAppOpen] = useState(false);
  const [newAppForm, setNewAppForm] = useState(EMPTY_NEW_APP);
  const [newAppSubmitting, setNewAppSubmitting] = useState(false);
  const [newAppError, setNewAppError] = useState('');
  const [questionSets, setQuestionSets] = useState([]);
  const [jobOpenings, setJobOpenings] = useState([]);

  useEffect(() => {
    listJobOpenings()
      .then((res) => setJobOpenings((Array.isArray(res.data) ? res.data : []).filter((j) => j.status === 'OPEN')))
      .catch(() => setJobOpenings([]));
  }, []);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [setName, setSetName] = useState('');
  const [setQuestionIds, setSetQuestionIds] = useState([]);
  const [creatingSet, setCreatingSet] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [aptitudeTests, setAptitudeTests] = useState([]);

  const loadApplicationLists = useCallback(async () => {
    if (!selectedOrgId) {
      setRankings([]);
      setAllApplications([]);
      return;
    }
    const [rankedRes, allRes] = await Promise.all([
      listRankedApplications(selectedOrgId),
      listApplications(selectedOrgId),
    ]);
    setRankings(Array.isArray(rankedRes.data) ? rankedRes.data : []);
    setAllApplications(Array.isArray(allRes.data) ? allRes.data : []);
  }, [selectedOrgId]);

  useEffect(() => {
    let cancelled = false;
    listMyOrgs().then((res) => {
      if (!cancelled) {
        const orgs = Array.isArray(res.data) ? res.data : [];
        setOrganizations(orgs);
        // Remember the last organization used here (per browser); otherwise pick the first.
        let remembered = null;
        try { remembered = localStorage.getItem('samastix.applications.orgId'); } catch { /* storage unavailable */ }
        const initial = orgs.find((o) => o.id === remembered) || orgs[0];
        if (initial) setSelectedOrgId(initial.id);
      }
    }).catch((err) => console.warn('[ApplicationsPage] organizations unavailable:', err.message));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        await loadApplicationLists();
        if (cancelled) return;
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load applications.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadApplicationLists]);

  useEffect(() => {
    if (!newAppOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await listQuestionSets();
        const list = Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
        if (!cancelled) setQuestionSets(list.filter((set) => set.orgId == null || set.orgId === selectedOrgId));
      } catch (err) {
        console.warn('[ApplicationsPage] listQuestionSets failed:', err.message);
        if (!cancelled) setQuestionSets([]);
      }
    })();
    (async () => {
      try {
        const res = await listAptitudeTests();
        const list = Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
        if (!cancelled) setAptitudeTests(list);
      } catch (err) {
        console.warn('[ApplicationsPage] listAptitudeTests failed:', err.message);
        if (!cancelled) setAptitudeTests([]);
      }
    })();
    return () => { cancelled = true; };
  }, [newAppOpen, selectedOrgId]);

  useEffect(() => {
    if (!newAppOpen || !selectedOrgId) return;
    let cancelled = false;
    listQuestions({ size: 500, active: true }).then((res) => {
      const questions = res.data?.content ?? [];
      if (!cancelled) setAvailableQuestions(questions.filter((q) => q.orgId == null || q.orgId === selectedOrgId));
    }).catch((err) => console.warn('[ApplicationsPage] questions unavailable:', err.message));
    return () => { cancelled = true; };
  }, [newAppOpen, selectedOrgId]);

  const handleCreateSet = async () => {
    if (!setName.trim() || setQuestionIds.length === 0 || !selectedOrgId) {
      setNewAppError('Enter a set name and select at least one question.');
      return;
    }
    setCreatingSet(true);
    setNewAppError('');
    try {
      const res = await createQuestionSet({ orgId: selectedOrgId, name: setName.trim(), items: setQuestionIds.map((questionId) => ({ questionId })) });
      setQuestionSets((prev) => [...prev, res.data]);
      setNewAppForm((prev) => ({ ...prev, questionSetId: res.data.id }));
      setSetName('');
      setSetQuestionIds([]);
    } catch (err) {
      setNewAppError(err?.response?.data?.message || err.message || 'Could not create question set.');
    } finally {
      setCreatingSet(false);
    }
  };

  const source = activeTab === 'rankings' ? rankings : allApplications;
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return source;
    return source.filter((item) => {
      const name = (item.candidateName || '').toLowerCase();
      const email = (item.candidateEmail || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [source, search]);

  const selectedFeedback = selected?.interviewFeedback || {};
  const strengths = toList(selectedFeedback?.strengths);
  const improvements = toList(selectedFeedback?.improvements);
  const integrityMeta = integrityBadge(selected?.integrityStatus);

  const openNewApplicationModal = () => {
    setNewAppForm(EMPTY_NEW_APP);
    setSetName('');
    setSetQuestionIds([]);
    setNewAppError('');
    setNewAppOpen(true);
  };

  const closeNewApplicationModal = () => {
    if (newAppSubmitting) return;
    setNewAppOpen(false);
    setNewAppError('');
    setNewAppForm(EMPTY_NEW_APP);
  };

  const handleNewAppField = (field, value) => {
    setNewAppForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewApplicationSubmit = async (e) => {
    e.preventDefault();
    setNewAppError('');

    const candidateName = newAppForm.candidateName.trim();
    const candidateEmail = newAppForm.candidateEmail.trim();
    const jobTitle = newAppForm.jobTitle.trim();
    const resumeUrl = newAppForm.resumeUrl.trim();

    if (!candidateName) {
      setNewAppError('Candidate name is required.');
      return;
    }
    if (!candidateEmail) {
      setNewAppError('Candidate email is required.');
      return;
    }
    if (!jobTitle && !newAppForm.jobOpeningId) {
      setNewAppError('Choose a job opening or enter a job title.');
      return;
    }
    if (!newAppForm.scheduledAt) {
      setNewAppError('Scheduled date and time is required.');
      return;
    }
    if (!selectedOrgId) {
      setNewAppError('Select an organization first.');
      return;
    }

    let scheduledAtIso;
    try {
      scheduledAtIso = new Date(newAppForm.scheduledAt).toISOString();
    } catch {
      setNewAppError('Invalid scheduled date/time.');
      return;
    }

    const payload = {
      candidateName,
      candidateEmail,
      scheduledAt: scheduledAtIso,
    };
    if (jobTitle) payload.jobTitle = jobTitle;
    // Blueprint Phase 13: the job opening drives job context, blueprint and assessment plan.
    if (newAppForm.jobOpeningId) payload.jobOpeningId = newAppForm.jobOpeningId;
    // Legacy / manual mode: a fixed question set replaces the blueprint plan.
    if (newAppForm.questionSetId) payload.questionSetId = newAppForm.questionSetId;
    if (newAppForm.aptitudeTestId) payload.aptitudeTestId = newAppForm.aptitudeTestId;
    if (resumeUrl) payload.resumeUrl = resumeUrl;

    setNewAppSubmitting(true);
    try {
      await createApplication(payload, selectedOrgId);
      await loadApplicationLists();
      setNewAppOpen(false);
      setNewAppForm(EMPTY_NEW_APP);
      toast.success('Application created');
    } catch (err) {
      const msg =
        err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Failed to create application.';
      setNewAppError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setNewAppSubmitting(false);
    }
  };

  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">AI Interview</p>
          <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Applications</h1>
          <p className="text-sm font-mono text-ink-faint mt-0.5">{visible.length} records</p>
        </div>
        <div className="w-full md:w-[320px]">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by candidate name or email…"
            className="input"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="text-sm font-medium text-ink">Organization
          <select className="input mt-1" value={selectedOrgId} onChange={(e) => {
            setSelectedOrgId(e.target.value);
            setNewAppForm(EMPTY_NEW_APP);
            try { localStorage.setItem('samastix.applications.orgId', e.target.value); } catch { /* storage unavailable */ }
          }}>
            <option value="">Select organization…</option>
            {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
        </label>
        <div className="flex items-center gap-1.5 p-1 bg-line-soft/80 rounded-xl border border-line">
          <button
            type="button"
            onClick={() => setActiveTab('rankings')}
            className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition ${activeTab === 'rankings' ? 'bg-surface text-teal-700 shadow-xs' : 'text-ink-soft hover:text-ink'}`}
          >
            Rankings
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('all')}
            className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition ${activeTab === 'all' ? 'bg-surface text-teal-700 shadow-xs' : 'text-ink-soft hover:text-ink'}`}
          >
            All Applications
          </button>
        </div>
        <button
          type="button"
          onClick={openNewApplicationModal}
          disabled={!selectedOrgId}
          className="btn btn-md btn-primary inline-flex items-center justify-center gap-2 shrink-0 self-start sm:self-auto"
        >
          <span className="text-lg leading-none font-semibold" aria-hidden>+</span>
          New Application
        </button>
      </div>

      <Modal
        open={newAppOpen}
        onClose={closeNewApplicationModal}
        title="New application"
        size="lg"
        footer={(
          <>
            <button
              type="button"
              onClick={closeNewApplicationModal}
              disabled={newAppSubmitting}
              className="btn btn-secondary px-4 py-2 text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="new-application-form"
              disabled={newAppSubmitting}
              className="btn btn-primary inline-flex items-center gap-2 px-5 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {newAppSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" aria-hidden />
                  Creating…
                </>
              ) : (
                'Create application'
              )}
            </button>
          </>
        )}
      >
        <form id="new-application-form" onSubmit={handleNewApplicationSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="new-app-candidate-name" className="block text-sm font-medium text-slate-700 mb-1.5">
                Candidate name <span className="text-red-600">*</span>
              </label>
              <input
                id="new-app-candidate-name"
                type="text"
                required
                autoComplete="name"
                value={newAppForm.candidateName}
                onChange={(e) => handleNewAppField('candidateName', e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label htmlFor="new-app-candidate-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Candidate email <span className="text-red-600">*</span>
              </label>
              <input
                id="new-app-candidate-email"
                type="email"
                required
                autoComplete="email"
                value={newAppForm.candidateEmail}
                onChange={(e) => handleNewAppField('candidateEmail', e.target.value)}
                className="input w-full"
              />
            </div>
          </div>

          <div>
            <label htmlFor="new-app-job-opening" className="block text-sm font-medium text-slate-700 mb-1.5">
              Job opening
            </label>
            <select
              id="new-app-job-opening"
              value={newAppForm.jobOpeningId}
              onChange={(e) => {
                const job = jobOpenings.find((j) => j.id === e.target.value);
                handleNewAppField('jobOpeningId', e.target.value);
                if (job && !newAppForm.jobTitle) handleNewAppField('jobTitle', job.title);
              }}
              className="input w-full"
            >
              <option value="">No job opening (external role)</option>
              {jobOpenings.filter((j) => !selectedOrgId || j.orgId === selectedOrgId).map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title}{j.pinnedBlueprint ? ` · ${j.pinnedBlueprint.name} v${j.pinnedBlueprint.versionNo}` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">The job's interview blueprint decides the topics. Every candidate for the job gets the same assessment.</p>
          </div>

          <div>
            <label htmlFor="new-app-job-title" className="block text-sm font-medium text-slate-700 mb-1.5">
              Job title {!newAppForm.jobOpeningId && <span className="text-red-600">*</span>}
            </label>
            <input
              id="new-app-job-title"
              type="text"
              required={!newAppForm.jobOpeningId}
              value={newAppForm.jobTitle}
              onChange={(e) => handleNewAppField('jobTitle', e.target.value)}
              className="input w-full"
            />
          </div>

          <div>
            <label htmlFor="new-app-scheduled-at" className="block text-sm font-medium text-slate-700 mb-1.5">
              Scheduled at <span className="text-red-600">*</span>
            </label>
            <input
              id="new-app-scheduled-at"
              type="datetime-local"
              required
              value={newAppForm.scheduledAt}
              onChange={(e) => handleNewAppField('scheduledAt', e.target.value)}
              className="input w-full"
            />
          </div>

          <div>
            <label htmlFor="new-app-question-set" className="block text-sm font-medium text-slate-700 mb-1.5">
              Fixed question set <span className="text-slate-400 font-normal">(optional, replaces the blueprint)</span>
            </label>
            <select
              id="new-app-question-set"
              value={newAppForm.questionSetId}
              onChange={(e) => handleNewAppField('questionSetId', e.target.value)}
              className="input w-full"
            >
              <option value="">None: use the interview blueprint</option>
              {questionSets.map((qs) => (
                <option key={qs.id} value={qs.id}>
                  {qs.name || qs.title || qs.id}
                </option>
              ))}
            </select>

            <details className="mt-3 rounded-lg border border-slate-200 p-3">
              <summary className="cursor-pointer text-sm font-medium">Create a question set for this organization</summary>
              <input className="input w-full mt-3" placeholder="Question set name" value={setName} onChange={(e) => setSetName(e.target.value)} />
              <p className="text-xs text-slate-500 mt-2">Choose questions for this organization. Your choices stay in the order selected.</p>
              <div className="max-h-44 overflow-y-auto mt-2 space-y-1">
                {availableQuestions.map((q) => <label key={q.id} className="flex gap-2 text-sm">
                  <input type="checkbox" checked={setQuestionIds.includes(q.id)} onChange={(e) => setSetQuestionIds((prev) => e.target.checked ? [...prev, q.id] : prev.filter((id) => id !== q.id))} />
                  {q.title}
                </label>)}
              </div>
              <button type="button" className="btn btn-secondary mt-3" disabled={creatingSet} onClick={handleCreateSet}>Create question set</button>
            </details>
          </div>

          <div>
            <label htmlFor="new-app-aptitude-test" className="block text-sm font-medium text-slate-700 mb-1.5">
              Aptitude test <span className="text-slate-500 font-normal">(optional override)</span>
            </label>
            <select
              id="new-app-aptitude-test"
              value={newAppForm.aptitudeTestId}
              onChange={(e) => handleNewAppField('aptitudeTestId', e.target.value)}
              className="input w-full"
            >
              <option value="">Use org default / none</option>
              {aptitudeTests.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || t.id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="new-app-resume-url" className="block text-sm font-medium text-slate-700 mb-1.5">
              Resume URL <span className="text-slate-500 font-normal">(optional)</span>
            </label>
            <input
              id="new-app-resume-url"
              type="text"
              value={newAppForm.resumeUrl}
              onChange={(e) => handleNewAppField('resumeUrl', e.target.value)}
              placeholder="https://drive.google.com/..."
              className="input w-full"
            />
          </div>

          {newAppError ? (
            <p className="text-red-600 text-sm" role="alert">{newAppError}</p>
          ) : null}
        </form>
      </Modal>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16"><Spinner size="md" label="Loading applications..." /></div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-red-600" role="alert">{error}</div>
        ) : activeTab === 'rankings' && visible.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-base font-semibold text-slate-700">No completed interviews yet.</p>
            <p className="text-sm text-slate-500 mt-1">Candidates will appear here after completing their AI interview.</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">No applications found.</div>
        ) : (
          <div className="overflow-x-auto">
            {activeTab === 'rankings' ? (
              <table className="w-full text-sm">
                <thead className="bg-canvas border-b border-line">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Candidate</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Job Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Interview</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Integrity</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Rec.</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-soft">
                  {visible.map((s, index) => {
                    const integrity = integrityBadge(s.integrityStatus);
                    return (
                      <tr
                        key={s.id}
                        onClick={() => setSelected(s)}
                        className="hover:bg-teal-50/20 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 font-mono font-semibold text-ink-faint">{index + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-ink">{s.candidateName || s.candidateEmail}</p>
                          <p className="text-xs text-ink-faint mt-0.5">{s.candidateEmail}</p>
                          {s.requiresHrReview && (
                            <span className="inline-flex mt-1.5 items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-200">
                              HR review
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-ink-soft">{s.jobTitle || '—'}</td>
                        <td className={`px-4 py-3 text-base font-mono font-bold ${scoreClass(s.interviewScore)}`}>
                          <div className="flex flex-col gap-1">
                            <span>
                              {s.interviewScore == null ? '—' : `${Math.round(s.interviewScore)}/100`}
                            </span>
                            {s.terminationReason === 'INTEGRITY_VIOLATION' && (
                              <span className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200">
                                Integrity review
                              </span>
                            )}
                            {isTechnicalFailure(s) && (
                              <span className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-slate-50 text-slate-600 border border-slate-200">
                                Not assessed (technical)
                              </span>
                            )}
                            {isTechnicalInterruption(s) && (
                              <span className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-slate-50 text-slate-600 border border-slate-200">
                                Interrupted (technical)
                              </span>
                            )}
                            {s.interviewIncomplete && s.questionsTotal > 0 && (
                              <span className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200">
                                Incomplete ({s.questionsReached ?? 0}/{s.questionsTotal})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wide ${integrity.cls}`}>
                            {integrity.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wide ${recommendationClass(s.hiringRecommendation)}`}>
                            {s.hiringRecommendation || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-ink-faint">{formatDate(s.completedAt || s.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-canvas border-b border-line">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Candidate</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Job Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Session Code</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Applied</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-soft">
                  {visible.map((s) => (
                    <tr
                      key={s.id}
                      onClick={() => setSelected(s)}
                      className="hover:bg-teal-50/20 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold text-ink">{s.candidateName || s.candidateEmail}</p>
                        <p className="text-xs text-ink-faint mt-0.5">{s.candidateEmail}</p>
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{s.jobTitle || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex w-fit items-center text-[11px] font-semibold uppercase tracking-wide ${statusClass(s.status)}`}>
                            {statusLabel(s.status)}
                          </span>
                          {aptitudeSummary(s) && (
                            <span className={`text-xs ${s.aptitudePassed === false ? 'text-rose-600' : 'text-ink-soft'}`}>
                              {aptitudeSummary(s)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-ink-soft">{s.sessionCode || '—'}</td>
                      <td className="px-4 py-3 text-xs font-mono text-ink-faint">{formatDate(s.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        width="lg"
        title={selected ? `${selected.candidateName || selected.candidateEmail} — ${selected.jobTitle || 'Interview'}` : 'Application'}
      >
        {!selected ? null : (
          <div className="space-y-4">
            {selected.requiresHrReview && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                {hrReviewReason(selected)}
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div className="rounded-lg border border-slate-200 bg-white p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1 font-semibold uppercase tracking-wide">Interview Score</p>
                  {selected.interviewScore != null ? (
                    <p className={`text-2xl font-bold ${scoreClass(selected.interviewScore)}`}>
                      {Math.round(selected.interviewScore)}
                      <span className="text-sm font-normal text-slate-500">/100</span>
                    </p>
                  ) : (
                    <p className="text-sm text-slate-400 italic">
                      {isTechnicalFailure(selected) ? 'Not assessed (technical issue)' : 'Scoring in progress…'}
                    </p>
                  )}
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1 font-semibold uppercase tracking-wide">Integrity signals (unreviewed)</p>
                  <div className="mt-1 flex justify-center">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${integrityMeta.cls}`}
                    >
                      {integrityMeta.label}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-semibold uppercase tracking-wide ${recommendationClass(selected.hiringRecommendation)}`}>
                  {selected.hiringRecommendation || '-'}
                </span>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Application Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-sm">
                <div><p className="text-slate-500">Candidate Email</p><p className="text-slate-800 font-medium">{selected.candidateEmail || '-'}</p></div>
                <div><p className="text-slate-500">Job Title</p><p className="text-slate-800 font-medium">{selected.jobTitle || '-'}</p></div>
                <div><p className="text-slate-500">Applied On</p><p className="text-slate-800 font-medium">{formatDate(selected.createdAt)}</p></div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <div className="mt-1 flex flex-col gap-1">
                    <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${statusClass(selected.status)}`}>
                      {statusLabel(selected.status)}
                    </span>
                    {aptitudeSummary(selected) && (
                      <p className={`text-sm font-medium ${selected.aptitudePassed === false ? 'text-red-600' : 'text-slate-700'}`}>
                        {aptitudeSummary(selected)}
                      </p>
                    )}
                  </div>
                </div>
                <div><p className="text-slate-500">Session Code</p><p className="text-slate-800 font-medium">{selected.sessionCode || '-'}</p></div>
              </div>
            </div>

            {selected.interviewFeedback && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">AI Interview Feedback</p>
                <div>
                  <p className="text-sm text-slate-700">{selectedFeedback.summary || '-'}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Strengths</p>
                    {strengths.length ? (
                      <ul className="space-y-1">
                        {strengths.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-sm text-slate-400">-</p>}
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Improvements</p>
                    {improvements.length ? (
                      <ul className="space-y-1">
                        {improvements.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-sm text-slate-400">-</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-[11px] uppercase text-slate-500 font-semibold">Technical</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{selectedFeedback.technicalScore ?? '-'}/10</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-[11px] uppercase text-slate-500 font-semibold">Approach</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{selectedFeedback.approachScore ?? '-'}/10</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-[11px] uppercase text-slate-500 font-semibold">Communication</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{selectedFeedback.communicationScore ?? '-'}/10</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-[11px] uppercase text-slate-500 font-semibold">Knowledge</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{selectedFeedback.knowledgeScore ?? '-'}/10</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
